import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  BackofficePageModeActionMenu,
  CustomizableDetailTabs,
  PageHeader,
  panelSpanFromLayout,
  SingleValuePanel,
  useCustomizableDetailTabs,
  useBackofficePageMode,
  TagsTile,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { fetchBackofficeInterviewDetail } from './api';
import {
  InterviewDetailPanelGrid,
  InterviewDetailsPanel,
  InterviewRecordingPanel,
  ParticipantDetailsPanel,
  InterviewSystemPromptPanel,
  InterviewTranscriptPanel,
} from './components';
import { formatDuration, formatShortId, formatStatus } from './format';
import type { BackofficeInterviewDetail } from './types';

const INTERVIEW_DETAIL_GRID_COLS = {
  xxl: 5,
  lg: 5,
};
const TRANSCRIPT_REFRESH_INTERVAL_MS = 5_000;
const MAX_TRANSCRIPT_REFRESH_REQUESTS = 24;
const MAX_CALL_REFRESH_DURATION_MS = 3 * 60_000;

type InterviewTab = 'overview' | 'recording' | 'settings';

const INTERVIEW_BUILT_IN_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'recording', label: 'Recording' },
  { id: 'settings', label: 'Settings' },
] satisfies Array<{ id: InterviewTab; label: string }>;

function parseRecordingTime(value: string | null): number | undefined {
  if (value === null) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : undefined;
}

export function BackofficeInterviewDetailsPage() {
  const { interviewId } = useParams<{ interviewId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const callRefreshDeadline = useMemo(
    () => ({ interviewId, expiresAt: Date.now() + MAX_CALL_REFRESH_DURATION_MS }),
    [interviewId]
  );
  const interviewQuery = useQuery({
    queryKey: ['backoffice', 'interviews', interviewId],
    queryFn: ({ signal }) => fetchBackofficeInterviewDetail(interviewId!, signal),
    enabled: Boolean(interviewId),
    refetchInterval: (query) => {
      const call = query.state.data?.interview.outboundCall;
      if (call && (call.status !== 'ended' || call.resultsStatus === 'pending')) {
        return Date.now() + TRANSCRIPT_REFRESH_INTERVAL_MS < callRefreshDeadline.expiresAt
          ? TRANSCRIPT_REFRESH_INTERVAL_MS
          : false;
      }
      const processingStatus = query.state.data?.interview.qualityProcessingStatus;
      const isTranscriptProcessing =
        processingStatus === 'pending' || processingStatus === 'processing';
      return isTranscriptProcessing &&
        query.state.dataUpdateCount <= MAX_TRANSCRIPT_REFRESH_REQUESTS
        ? TRANSCRIPT_REFRESH_INTERVAL_MS
        : false;
    },
  });
  const interview = interviewQuery.data?.interview ?? null;
  const error = !interviewId
    ? 'Interview not found'
    : interviewQuery.error instanceof Error
      ? interviewQuery.error.message
      : interviewQuery.error
        ? 'Failed to load interview'
        : null;
  const loading = Boolean(interviewId) && interviewQuery.isPending;
  const initialRecordingTimeSeconds = parseRecordingTime(searchParams.get('recordingTime'));
  const { pageMode, setPageMode } = useBackofficePageMode();
  const detailBasePath = interviewId ? `/interviews/${interviewId}` : '/interviews';
  const detailTabs = useCustomizableDetailTabs<InterviewTab>({
    preferenceScope: 'details:interview:tabs',
    builtInTabs: INTERVIEW_BUILT_IN_TABS,
    defaultTabId: initialRecordingTimeSeconds !== undefined ? 'recording' : 'overview',
    defaultHrefTabId: 'overview',
    clearSearchParamsByTabId: { overview: ['recordingTime'] },
    basePath: detailBasePath,
    pageMode,
  });
  const activeTab = detailTabs.activeBuiltInTabId;
  const shortId = interview
    ? formatShortId(interview.id)
    : interviewId
      ? formatShortId(interviewId)
      : 'Interview';
  const conversationTranscript = useMemo(
    () => interview?.transcript.filter((message) => message.role !== 'system') ?? [],
    [interview?.transcript]
  );
  const systemPrompt = useMemo(
    () =>
      interview?.transcript
        .filter((message) => message.role === 'system')
        .map((message) => message.content.trim())
        .filter((content) => content.length > 0)
        .join('\n\n') ?? '',
    [interview?.transcript]
  );

  useBackofficePageTitle(interview ? `Interview: ${interview.id}` : 'Interview');

  const createInterviewTileItems = (loadedInterview: BackofficeInterviewDetail) => [
    {
      id: 'status',
      title: 'Status',
      description: 'Current interview processing and completion status.',
      layout: { x: 0, y: 0, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <SingleValuePanel
          panelId="status"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
          title="Status"
          value={formatStatus(loadedInterview.status)}
        />
      ),
    },
    {
      id: 'duration',
      title: 'Duration',
      description: 'Total recorded interview duration.',
      layout: { x: 1, y: 0, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <SingleValuePanel
          panelId="duration"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
          title="Duration"
          value={formatDuration(loadedInterview.durationSeconds)}
        />
      ),
    },
    {
      id: 'quality-level',
      title: 'Quality level',
      description: 'Success evaluation score for the interview.',
      layout: { x: 2, y: 0, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <SingleValuePanel
          panelId="quality-level"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
          title="Quality level"
          value={
            loadedInterview.successEvaluation === null
              ? 'N/A'
              : String(loadedInterview.successEvaluation)
          }
        />
      ),
    },
    {
      id: 'interview-details',
      title: 'Interview details',
      description: 'Full interview metadata, timings, and recording data.',
      layout: {
        x: 0,
        y: 1,
        w: 2,
        h: 5,
        minW: 1,
        minH: 1,
        maxW: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <InterviewDetailsPanel
          interview={loadedInterview}
          panelId="interview-details"
          currentSpan={panelSpanFromLayout(layout, '2x5')}
        />
      ),
    },
    {
      id: 'participant-details',
      title: 'Participant details',
      description: 'Participant identity, assignment, panel submission, and live provider status.',
      layout: {
        x: 2,
        y: 1,
        w: 2,
        h: 5,
        minW: 2,
        minH: 3,
        maxW: 2,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <ParticipantDetailsPanel
          interviewId={loadedInterview.id}
          panelId="participant-details"
          currentSpan={panelSpanFromLayout(layout, '2x5')}
        />
      ),
    },
    {
      id: 'recording',
      title: 'Recording',
      description: 'Audio player for the recorded interview session.',
      layout: {
        x: 0,
        y: 6,
        w: 3,
        h: 2,
        minW: 1,
        maxW: 5,
        minH: 1,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <InterviewRecordingPanel
          panelId="recording"
          currentSpan={panelSpanFromLayout(layout, '3x2')}
          recordingUrl={loadedInterview.recordingUrl}
          waveformSourceUrl={loadedInterview.waveformSourceUrl}
          waveformPeaks={loadedInterview.waveformPeaks}
          durationSeconds={
            loadedInterview.recordingMedia?.durationSeconds ?? loadedInterview.durationSeconds
          }
          initialSeekTimeSeconds={initialRecordingTimeSeconds}
          preferenceScope="details:interview:recording:recording"
          shareLocation={location}
        />
      ),
    },
    {
      id: 'transcription',
      title: 'Transcript',
      description: 'Timestamped conversation transcript for the interview.',
      layout: { x: 0, y: 8, w: 3, h: 3, minW: 1, maxW: 5, minH: 1, isResizable: true },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <InterviewTranscriptPanel
          panelId="transcription"
          currentSpan={panelSpanFromLayout(layout, '3x3')}
          transcript={conversationTranscript}
          preferenceScope="details:interview:recording:transcription"
          shareLocation={location}
        />
      ),
    },
    {
      id: 'system-prompt',
      title: 'System prompt',
      description: 'System prompt messages used to guide the interview.',
      layout: { x: 0, y: 11, w: 3, h: 5, minW: 1, minH: 1, isResizable: true },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <InterviewSystemPromptPanel
          panelId="system-prompt"
          systemPrompt={systemPrompt}
          currentSpan={panelSpanFromLayout(layout, '3x5')}
        />
      ),
    },
    {
      id: 'tags',
      title: 'Tags',
      description: 'Tags assigned to this interview.',
      layout: {
        x: 0,
        y: 16,
        w: 2,
        h: 1,
        minW: 1,
        minH: 1,
        maxW: 3,
        maxH: 3,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <TagsTile
          entityType="interview"
          entityId={loadedInterview.id}
          panelId="tags"
          currentSpan={panelSpanFromLayout(layout, '2x1')}
        />
      ),
    },
  ];

  return (
    <main className="min-h-[320px] bg-neutral-50 p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          actions={
            <BackofficePageModeActionMenu
              pageMode={pageMode}
              onPageModeChange={setPageMode}
              label="Interview page actions"
            />
          }
          showShareButton={pageMode !== 'edit'}
        >
          {interview ? `Interview ${formatShortId(interview.id)}` : `Interview ${shortId}`}
        </PageHeader>

        <CustomizableDetailTabs tabs={detailTabs} />

        {loading ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-neutral-500 shadow-sm">
            Loading interview
          </div>
        ) : error ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-red-700 shadow-sm">
            {error}
          </div>
        ) : interview && activeTab === 'overview' ? (
          <InterviewDetailPanelGrid
            resetKey={interview.id}
            preferenceScope="details:interview:overview"
            cols={INTERVIEW_DETAIL_GRID_COLS}
            layoutMode={pageMode}
            defaultVisibleItemIds={['status', 'duration', 'quality-level', 'interview-details']}
            tileCatalogLabel="Add interview tile"
            items={createInterviewTileItems(interview)}
          />
        ) : interview && detailTabs.activeCustomTab ? (
          <InterviewDetailPanelGrid
            resetKey={detailTabs.activeCustomTab.id}
            preferenceScope={`details:interview:${detailTabs.activeCustomTab.id}`}
            cols={INTERVIEW_DETAIL_GRID_COLS}
            layoutMode={pageMode}
            defaultVisibleItemIds={[]}
            tileCatalogLabel="Add interview tile"
            items={createInterviewTileItems(interview)}
          />
        ) : interview && activeTab === 'recording' ? (
          <InterviewDetailPanelGrid
            resetKey={`${interview.id}:recording`}
            preferenceScope="details:interview:recording"
            cols={INTERVIEW_DETAIL_GRID_COLS}
            layoutMode={pageMode}
            defaultVisibleItemIds={['recording', 'transcription']}
            tileCatalogLabel="Add interview tile"
            items={createInterviewTileItems(interview)}
          />
        ) : interview && activeTab === 'settings' ? (
          <InterviewDetailPanelGrid
            resetKey={`${interview.id}:settings`}
            preferenceScope="details:interview:settings"
            layoutMode={pageMode}
            cols={INTERVIEW_DETAIL_GRID_COLS}
            defaultVisibleItemIds={['system-prompt']}
            tileCatalogLabel="Add interview tile"
            items={createInterviewTileItems(interview)}
          ></InterviewDetailPanelGrid>
        ) : null}
      </div>
    </main>
  );
}
